import * as THREE from 'three';
import { configureShadowRenderer } from './shadowConfig';
import { updateLighting } from './lighting';
import {
	enhanceRendererForRayTracing,
	createAdvancedLighting,
	RAY_TRACING_CONFIG,
	createDynamicEnvironmentMap,
} from './rayTracing';

export const RENDERER_CONFIG = {
	canvasHeight: 650,
	antialias: true,
	clearColor: 0x87ceeb,
	maxPixelRatio: 2,
	targetFPS: 60,
	localClippingEnabled: true,
} as const;

export const LIGHT_CONFIG = {
	ambientIntensity: RAY_TRACING_CONFIG.enabled ? 0.2 : 0.4,
	directionalIntensity: RAY_TRACING_CONFIG.enabled ? 2.5 : 1.8,
	rayTracingEnabled: RAY_TRACING_CONFIG.enabled,
} as const;

/**
 * Creates and configures a WebGL renderer
 */
export const setupRenderer = (
	canvas: HTMLCanvasElement,
	size?: { width: number; height: number }
): THREE.WebGLRenderer => {
	const container = canvas.parentElement!;
	const width = size?.width || container.clientWidth;
	const height = size?.height || container.clientHeight || RENDERER_CONFIG.canvasHeight;

	const renderer = new THREE.WebGLRenderer({
		canvas,
		antialias: RENDERER_CONFIG.antialias,
		alpha: true,
	});

	renderer.localClippingEnabled = RENDERER_CONFIG.localClippingEnabled;
	renderer.setSize(width, height);
	renderer.setPixelRatio(Math.min(window.devicePixelRatio, RENDERER_CONFIG.maxPixelRatio));
	renderer.setClearColor(RENDERER_CONFIG.clearColor, 1.0);

	configureShadowRenderer(renderer);

	renderer.sortObjects = true;
	renderer.outputColorSpace = THREE.SRGBColorSpace;

	renderer.toneMapping = THREE.ACESFilmicToneMapping;
	renderer.toneMappingExposure = RAY_TRACING_CONFIG.enabled ? 1.0 : 1.2;

	if (RAY_TRACING_CONFIG.enabled) {
		enhanceRendererForRayTracing(renderer);
	}

	return renderer;
};

/**
 * Creates and configures scene lighting with optional ray tracing enhancements
 */
export const setupLights = (
	scene: THREE.Scene,
	renderer: THREE.WebGLRenderer,
	sunPosition: THREE.Vector3
): { ambientLight: THREE.AmbientLight; directionalLight: THREE.DirectionalLight } => {
	if (LIGHT_CONFIG.rayTracingEnabled && renderer) {
		const envMap = createDynamicEnvironmentMap(renderer, sunPosition);
		const advancedLights = createAdvancedLighting(scene, envMap);

		return {
			ambientLight: advancedLights.ambientLight,
			directionalLight: advancedLights.directionalLight,
		};
	} else {
		const ambientLight = new THREE.AmbientLight(0xffffff, LIGHT_CONFIG.ambientIntensity);
		scene.add(ambientLight);

		const directionalLight = new THREE.DirectionalLight(
			0xffffff,
			LIGHT_CONFIG.directionalIntensity
		);

		scene.add(directionalLight);
		scene.add(directionalLight.target);

		return { ambientLight, directionalLight };
	}
};

/**
 * Initialize lighting with current settings
 */
export const initializeLighting = (
	sceneRef: React.MutableRefObject<THREE.Scene | null>,
	ambientLightRef: React.MutableRefObject<THREE.AmbientLight | null>,
	directionalLightRef: React.MutableRefObject<THREE.DirectionalLight | null>,
	rendererRef: React.MutableRefObject<THREE.WebGLRenderer | null>,
	starsRef: React.MutableRefObject<THREE.Points | null>,
	currentSunPositionRef: React.MutableRefObject<THREE.Vector3>,
	currentDate: Date,
	currentTime: string,
	latitude: number,
	longitude: number
): void => {
	if (!sceneRef.current) return;

	const { ambientLight, directionalLight } = setupLights(
		sceneRef.current,
		rendererRef.current!,
		currentSunPositionRef.current
	);
	ambientLightRef.current = ambientLight;
	directionalLightRef.current = directionalLight;

	updateLighting(
		sceneRef.current,
		ambientLight,
		directionalLight,
		rendererRef.current!,
		starsRef.current,
		currentSunPositionRef,
		currentDate,
		currentTime,
		latitude,
		longitude
	);
};
